import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, AlertTriangle, Clock, Loader2 } from "lucide-react";
import { getAuthHeaders } from "@/utils/auth";
import { MapContainer, TileLayer, useMap, Popup, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const USER_FILTERS = [
  { label: "7 Days", days: 7 },
  { label: "30 Days", days: 30 },
  { label: "90 Days", days: 90 },
  { label: "1 Year", days: 365 },
  { label: "All Time", days: null }
];

const POLICE_FILTERS = [
  { label: "48 Hrs", days: 2 },
  { label: "7 Days", days: 7 },
  { label: "30 Days", days: 30 },
  { label: "90 Days", days: 90 },
  { label: "1 Year", days: 365 },
  { label: "All Time", days: null }
];

function HeatLayer({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!points.length) return;

    let heat;
    import("leaflet.heat").then(() => {
      const heatData = points.map(p => [p.lat, p.lng, 1]);
      heat = L.heatLayer(heatData, {
        radius: 30,
        blur: 20,
        maxZoom: 12,
        max: 1.0,
        gradient: {
          0.2: "#1a237e",
          0.4: "#0d47a1",
          0.6: "#f9a825",
          0.8: "#ff6f00",
          1.0: "#d32f2f"
        }
      }).addTo(map);
    });

    return () => {
      if (heat) map.removeLayer(heat);
    };
  }, [map, points]);

  return null;
}

function ClusterMarkers({ points }) {
  const clusters = useMemo(() => {
    const groups = {};
    for (const p of points) {
      if (!groups[p.outcode]) {
        groups[p.outcode] = { lat: 0, lng: 0, count: 0, tools: [], earliest: null, latest: null };
      }
      const g = groups[p.outcode];
      g.lat += p.lat;
      g.lng += p.lng;
      g.count += 1;
      g.tools.push(p);
      if (p.date) {
        if (!g.earliest || p.date < g.earliest) g.earliest = p.date;
        if (!g.latest || p.date > g.latest) g.latest = p.date;
      }
    }
    for (const key of Object.keys(groups)) {
      groups[key].lat /= groups[key].count;
      groups[key].lng /= groups[key].count;
    }
    return groups;
  }, [points]);

  const formatDate = (iso) => {
    if (!iso) return "Unknown";
    try {
      return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  return Object.entries(clusters).map(([outcode, data]) => (
    <CircleMarker
      key={outcode}
      center={[data.lat, data.lng]}
      radius={Math.min(8 + data.count * 2, 25)}
      pathOptions={{
        fillColor: data.count >= 5 ? "#d32f2f" : data.count >= 3 ? "#ff6f00" : "#f9a825",
        fillOpacity: 0.7,
        color: "#fff",
        weight: 1
      }}
    >
      <Popup>
        <div className="text-xs" style={{ minWidth: "160px" }}>
          <p style={{ fontWeight: "bold", fontSize: "14px", marginBottom: "4px" }}>{outcode}</p>
          <p style={{ color: "#d32f2f", fontWeight: "600" }}>{data.count} stolen tool{data.count !== 1 ? "s" : ""}</p>
          <p style={{ color: "#666", marginTop: "4px" }}>
            {formatDate(data.earliest)}{data.earliest !== data.latest ? ` — ${formatDate(data.latest)}` : ""}
          </p>
          <div style={{ marginTop: "6px", borderTop: "1px solid #eee", paddingTop: "4px" }}>
            {data.tools.slice(0, 3).map((t, i) => (
              <p key={i} style={{ color: "#444" }}>{t.manufacturer} {t.tool_type}</p>
            ))}
            {data.tools.length > 3 && (
              <p style={{ color: "#999", fontStyle: "italic" }}>+{data.tools.length - 3} more</p>
            )}
          </div>
        </div>
      </Popup>
    </CircleMarker>
  ));
}

function FitBounds({ points }) {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) return;
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 10 });
  }, [map, points]);

  return null;
}

export default function TheftHeatmap({ isPolice = false, customAuthHeaders = null }) {
  const filters = isPolice ? POLICE_FILTERS : USER_FILTERS;
  const [activeDays, setActiveDays] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const endpoint = isPolice ? "/police/heatmap/data" : "/heatmap/data";
        const params = activeDays !== null ? `?days=${activeDays}` : "";
        const headers = customAuthHeaders || getAuthHeaders();
        const response = await fetch(`${API}${endpoint}${params}`, { headers });
        if (response.ok) {
          setData(await response.json());
        }
      } catch (e) {
        console.error("Heatmap data error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isPolice, activeDays, customAuthHeaders]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  const activeLabel = filters.find(f => f.days === activeDays)?.label || "All Time";

  if (loading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
          <span className="text-muted-foreground">Loading heatmap data...</span>
        </CardContent>
      </Card>
    );
  }

  const hasPoints = data && data.points && data.points.length > 0;

  return (
    <div className="space-y-4" data-testid="theft-heatmap">
      {/* Map */}
      <Card className="border-border bg-card overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <CardTitle className="font-heading text-lg md:text-xl uppercase tracking-wider flex items-center gap-2 flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              Theft Heatmap
              {isPolice && data?.total_stolen > 0 && (
                <Badge variant="outline" className="ml-2 text-xs border-red-500/30 text-red-400">
                  {data.total_stolen} stolen — £{(data.total_value || 0).toLocaleString("en-GB")}
                </Badge>
              )}
            </CardTitle>
            <div className="flex gap-1 flex-nowrap overflow-x-auto sm:ml-auto" data-testid="heatmap-filters">
              {filters.map(f => (
                <Button
                  key={f.label}
                  variant={activeDays === f.days ? "default" : "outline"}
                  size="sm"
                  className="h-6 px-2 text-[11px] whitespace-nowrap flex-shrink-0"
                  onClick={() => setActiveDays(f.days)}
                  data-testid={`heatmap-filter-${f.days ?? "all"}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!hasPoints ? (
            <div className="py-12 text-center">
              <MapPin className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground text-sm">No stolen tools reported in the {activeLabel.toLowerCase()} period.</p>
            </div>
          ) : (
            <div style={{ height: isPolice ? "500px" : "380px" }} data-testid="heatmap-container">
              <MapContainer
                center={[53.5, -1.5]}
                zoom={6}
                style={{ height: "100%", width: "100%", borderRadius: "0 0 0.5rem 0.5rem" }}
                scrollWheelZoom={true}
                zoomControl={true}
              >
                <TileLayer
                  attribution='Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Esri, DeLorme, NAVTEQ'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={16}
                />
                <HeatLayer points={data.points} />
                <ClusterMarkers points={data.points} />
                <FitBounds points={data.points} />
              </MapContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top 10 Areas */}
      {data?.top_areas?.length > 0 && (
        <Card className="border-border bg-card" data-testid="top-areas-table">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg md:text-xl uppercase tracking-wider flex items-center gap-2">
              <MapPin className="w-4 h-4 text-red-400" />
              Top {data.top_areas.length} Worst Affected Areas
              <span className="text-xs font-normal text-muted-foreground ml-1">({activeLabel})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/20">
                    <th className="text-left pl-4 pr-1 py-3 font-heading text-xs uppercase tracking-wider text-muted-foreground w-10">#</th>
                    <th className="text-left px-2 py-3 font-heading text-xs uppercase tracking-wider text-muted-foreground w-16">Area</th>
                    <th className="text-center px-2 py-3 font-heading text-xs uppercase tracking-wider text-muted-foreground w-14">Stolen</th>
                    {isPolice && (
                      <>
                        <th className="text-right px-2 py-3 font-heading text-xs uppercase tracking-wider text-muted-foreground w-20">Value</th>
                        <th className="text-center px-2 py-3 font-heading text-xs uppercase tracking-wider text-muted-foreground w-16">Victims</th>
                      </>
                    )}
                    <th className="text-left px-2 pr-4 py-3 font-heading text-xs uppercase tracking-wider text-muted-foreground">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Period
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_areas.map((area, idx) => (
                    <tr key={area.outcode} className="border-b border-border/50 hover:bg-muted/10 transition-colors">
                      <td className="pl-4 pr-1 py-3">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                          idx === 0 ? "bg-red-500 text-white" :
                          idx === 1 ? "bg-orange-500 text-white" :
                          idx === 2 ? "bg-yellow-500 text-black" :
                          "bg-muted text-muted-foreground"
                        }`}>
                          {idx + 1}
                        </span>
                      </td>
                      <td className="px-2 py-3 font-heading font-bold text-base">{area.outcode}</td>
                      <td className="text-center px-2 py-3">
                        <Badge variant="outline" className="border-red-500/30 text-red-400">
                          {area.count}
                        </Badge>
                      </td>
                      {isPolice && (
                        <>
                          <td className="text-right px-2 py-3 font-mono text-primary text-xs">
                            £{(area.total_value || 0).toLocaleString("en-GB")}
                          </td>
                          <td className="text-center px-2 py-3 text-muted-foreground">
                            {area.affected_owners || "—"}
                          </td>
                        </>
                      )}
                      <td className="px-2 pr-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(area.earliest)}
                        {area.earliest !== area.latest && (
                          <> — {formatDate(area.latest)}</>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
